import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ApprovalLevelRequirementEnum } from 'shared/constants';
import { useApproverUsers, useSendDeviationNeedsApprovalEmail } from 'shared/hooks';
import { useEditDeviationRequest } from 'shared/hooks/useEditDeviationRequest';
import { useDistributor } from 'shared/providers';
import { DynamicEmailData } from 'shared/services/email-address-service';
import { useAuthState } from 'shared/stores';
import { TableDataContext } from 'shared/types';
import {
    determineApprovalLevelRequirement,
    determineApproverAndStatus,
    determineApprovers,
    determineVariance,
    formatFourDecimalMoney,
    isTodaysDate,
} from 'shared/utility';
import { CurrentPricesData, NeedsApprovalEmailData } from 'types/backend';
import { TAddedDeviation } from 'types/forms';
import { EditDeviationForm } from './editDeviationForm';
import { AccountNumberParams, managePricingContext } from './managePricingResolver';

interface EditDeviationFormProps {
    row?: CurrentPricesData;
    open: boolean;
    closeEditDeviationDialog: () => void;
}

export function EditDeviationFormDialog({ row, open, closeEditDeviationDialog }: EditDeviationFormProps) {
    const { enqueueSnackbar } = useSnackbar();
    const { accountNumber = '' } = useParams<AccountNumberParams>();
    const today = new Date();
    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
        setValue,
    } = useForm<TAddedDeviation>({
        defaultValues: row && {
            modelCurrentPrice: {
                currentpriceid: row.id.split('-')[0] || '0',
                accountnumber: accountNumber || '',
                enduseraccountnumber: '',
                enduserpricinggroup: row.priceGroup || '',
                pricetype: row.priceType || '',
                itemorgroup: row.productId || '',
                startdate: !isTodaysDate(row.start) ? today.toLocaleDateString() : row.start,
                enddate: row.end || '',
                price: row.price.toString() || '',
                status: 'Approved',
                approver: 'Not Required',
                estmonthlyrevenue: '',
                competitor: '',
                competitorprice: '',
                source: '',
                lastupdated: '',
                modifiedby: '',
                effectivestart: row.effectiveStart || '',
                effectiveend: '',
            },
            deviationDetails: {
                deviationReason: '',
                deviationType:
                    (!row.endUser || row.endUser === 'None') && !!row.priceGroup ? 'Pricing Group' : 'End User',
                isOneTimeOnly: false,
            },
            deviationReasonNotes: '',
            competitorProduct: '',
        },
    });
    const { data: approverUsers } = useApproverUsers();

    const { onSubmit, isLoading } = useEditDeviationRequest({ onSuccess: closeEditDeviationDialog });

    const authState = useAuthState();
    const currentUser = authState.user.get()?.displayName;
    const url = window.location.origin;

    const distributor = useDistributor();

    const dynamicEmailData: Omit<DynamicEmailData, 'currentUserName' | 'currentUserEmailAddress'> = {
        territorySalesLeaderName: distributor.matchingCustomer?.territorySalesLeader || '',
        territorySalesLeaderEmail: distributor.matchingCustomer?.territorySalesLeaderEmail || '',
        territorySalesRepName: distributor.matchingCustomer?.territorySalesRep || '',
        territorySalesRepEmail: distributor.matchingCustomer?.territorySalesRepEmail || '',
    };

    const sendEmail = useSendDeviationNeedsApprovalEmail(`${url}/approvals`, dynamicEmailData);

    const currentPricesContext = useContext<TableDataContext<CurrentPricesData>>(managePricingContext);
    const currentProductContext = currentPricesContext?.response?.data?.rows.find((currentPriceRow) => {
        return (
            currentPriceRow.productId === row?.productId &&
            currentPriceRow.priceType.toLowerCase() === 'market price' &&
            currentPriceRow.status.toLowerCase() === 'approved'
        );
    });

    const submitDeviationEdit = handleSubmit(async (form: TAddedDeviation) => {
        const formCopy: TAddedDeviation = JSON.parse(JSON.stringify(form));

        if (row) {
            const approvalLevel = determineApprovalLevelRequirement(
                row.tierOnePrice,
                Number(formCopy.modelCurrentPrice.price),
                distributor.matchingCustomer?.priceTier
            );

            if (approvalLevel !== ApprovalLevelRequirementEnum.NOTREQUIRED) {
                const approvers = determineApprovers(approverUsers, approvalLevel);

                const { approver, status } = determineApproverAndStatus(
                    approvers,
                    Number(row.tierOnePrice),
                    Number(formCopy.modelCurrentPrice.price),
                    distributor.matchingCustomer.priceTier
                );
                formCopy.modelCurrentPrice.approver = approver;
                formCopy.modelCurrentPrice.status = status;

                const emailData: NeedsApprovalEmailData[] = [
                    {
                        createdBy: currentUser ?? '',
                        customerName: distributor.matchingCustomer?.customerName ?? '',
                        customerAccount: distributor.matchingCustomer?.customerNumber ?? '',
                        productId: row.productId,
                        customerTier: distributor.matchingCustomer?.priceTier ?? '',
                        pricingGroup: formCopy.modelCurrentPrice.enduserpricinggroup,
                        isOneTimeOnly: formCopy.deviationDetails.isOneTimeOnly,
                        territorySalesLeader: distributor.matchingCustomer?.territorySalesLeader ?? '',
                        territorySalesRep: 'Not Yet Available',
                        newPrice: formatFourDecimalMoney(Number(formCopy.modelCurrentPrice.price)),
                        customerTierTargetPrice: formatFourDecimalMoney(row.targetPrice),
                        tierOneTargetPrice: formatFourDecimalMoney(row.tierOnePrice),
                        currentPrice: formatFourDecimalMoney(row.price),
                        variance: determineVariance(Number(formCopy.modelCurrentPrice.price), Number(row.targetPrice)),
                        estAnnualVolume: formCopy.modelCurrentPrice.estmonthlyrevenue,
                        deviationReason: formCopy.deviationDetails.deviationReason,
                        comments: formCopy.deviationReasonNotes ?? '',
                        approverEmails: approvers.map((a) => a.email),
                        approverLevel: approvalLevel.valueOf(),
                    },
                ];
                try {
                    await sendEmail.mutateAsync(emailData);
                } catch (e) {
                    enqueueSnackbar('Deviation editted email failed to send', { variant: 'error' });
                }
            }
        }

        onSubmit(formCopy);
        return closeEditDeviationDialog();
    });

    return (
        <Dialog open={open} aria-labelledby='responsive-dialog-title' maxWidth='xl'>
            <DialogTitle id='responsive-dialog-title'>{`Details for product: ${row?.productId}`}</DialogTitle>
            <DialogContent>
                <DialogContentText>Edit Deviation</DialogContentText>
                <Card key={row?.id} sx={{ border: '1px solid gray' }}>
                    <CardHeader
                        sx={{ backgroundColor: '#f1f1f1', borderBottom: '1px solid gray' }}
                        title={row?.productId}
                    />
                    <CardContent>
                        <Grid container spacing={2} sx={{ padding: '1rem 0' }}>
                            <Grid container item sx={{ backgroundColor: '#f1f1f1', padding: '1rem 2rem' }}>
                                {row && (
                                    <>
                                        <Grid item xs={3}>
                                            Current Deviated Price: <strong>{formatFourDecimalMoney(row.price)}</strong>
                                        </Grid>
                                        <Grid item xs={3}>
                                            Tier 1 Target Price:{' '}
                                            <strong>{formatFourDecimalMoney(row.tierOnePrice)}</strong>
                                        </Grid>
                                        <Grid item xs={3}>
                                            Customer Tier Target:{' '}
                                            <strong>{formatFourDecimalMoney(row.targetPrice)}</strong>
                                        </Grid>
                                        <Grid item xs={3}>
                                            {currentProductContext ? (
                                                <>
                                                    Current Market Price:{' '}
                                                    <strong>
                                                        {formatFourDecimalMoney(currentProductContext.price)}
                                                    </strong>
                                                </>
                                            ) : null}
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            <EditDeviationForm
                                control={control}
                                errors={errors}
                                watch={watch}
                                setValue={setValue}
                                row={row}
                            />
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={closeEditDeviationDialog}>
                    Exit
                </Button>
                <Button onClick={submitDeviationEdit} autoFocus disabled={isLoading}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}
